import PropTypes from 'prop-types'

import { ENVIO_A_CARGO_COMPRADOR, RETIRA_COMPRADOR } from '../../../constants/mediosEnviosPagos'
import { ChipList } from '../../components/index.js'
import styles from './MediosEnvioChips.module.css'

/**
 * Tooltip del envío a cargo del comprador: costo y plazo estimado por zona, tomando la primera
 * grilla que aparece para cada zona en las listas del vendedor.
 * @param {{ listaEnvios: Array }} props
 */
const TooltipZonas = ({ listaEnvios }) => {
  const zonaCostoMap = new Map()
  listaEnvios.forEach((envio) => {
    envio.grilla?.forEach((g) => {
      if (!zonaCostoMap.has(g.zona)) {
        zonaCostoMap.set(g.zona, { costo: g.costo, tEstimado: g.tEstimado })
      }
    })
  })

  return (
    <div>
      {Array.from(zonaCostoMap.entries()).map(([zona, det]) => (
        <div key={zona} className={styles.detalle}>
          {zona}: ${det.costo} - {det.tEstimado} días
        </div>
      ))}
      <div className={styles.aclaracion}>
        Continuar la compra para ver el detalle del costo de envío
      </div>
    </div>
  )
}

TooltipZonas.propTypes = {
  listaEnvios: PropTypes.array.isRequired
}

/**
 * Tooltip de los demás medios de envío: zonas cubiertas y plazo de entrega, si el medio los define.
 * @param {{ descZona?: string, cantDias?: number, aviso?: string }} props
 */
const LONGITUD_MAXIMA_ZONA = 40

const TooltipZonaDias = ({ descZona, cantDias, aviso }) => {
  const tieneDias = Number.isFinite(cantDias)
  const mostrarSaltoZona = typeof descZona === 'string' && descZona.length > LONGITUD_MAXIMA_ZONA

  return (
    <div>
      {descZona && (
        <div className={styles.detalle}>
          <strong>Zonas:</strong>
          {mostrarSaltoZona ? (
            <>
              <br />
              {descZona}
            </>
          ) : (
            ` ${descZona}`
          )}
        </div>
      )}
      {aviso && (
        <div className={styles.detalle}>
          <strong>Aviso:</strong> {aviso}
        </div>
      )}
      {tieneDias && (
        <div className={styles.detalle}>
          Aprox. {cantDias} día{Number(cantDias) > 1 ? 's' : ''} de entrega
        </div>
      )}
    </div>
  )
}

TooltipZonaDias.propTypes = {
  descZona: PropTypes.string,
  cantDias: PropTypes.number,
  aviso: PropTypes.string
}

/**
 * Lista de chips con los medios de envío de una publicación.
 * `listaEnvios` son las listas de envío del vendedor: si vienen, el chip de envío a cargo del
 * comprador muestra costo y plazo por zona en el tooltip.
 * @param {{ mediosEnvios?: Array, listaEnvios?: Array, title?: string }} props
 */
export const MediosEnvioChips = ({
  mediosEnvios = [],
  listaEnvios = [],
  title = 'Medios de Envío'
}) => {
  const items = mediosEnvios.flatMap((item) => {
    const label = item.medioEnvio?.nombre || item.nombre
    if (!label) return []

    const esACargoComprador = item.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
    const esHabilitado = item.habilitado
    const esRetiraComprador = item.idMedioEnvio === RETIRA_COMPRADOR
    const tieneDetalle = Boolean(item.descZona) || Number.isFinite(item.cantDias)

    let tooltip
    if (esACargoComprador && listaEnvios.length > 0) {
      tooltip = <TooltipZonas listaEnvios={listaEnvios} />
    } else if (!esHabilitado && esRetiraComprador) {
      tooltip = <TooltipZonaDias aviso="El vendedor no tiene cargado el domicilio de retiro" />
    } else if (tieneDetalle) {
      tooltip = <TooltipZonaDias descZona={item.descZona} cantDias={item.cantDias} />
    }

    return [
      {
        id: item.idMedioEnvio,
        label,
        tooltip,
        tooltipSize: 'large',
        variant: item.habilitado ? 'default' : 'warning'
      }
    ]
  })

  if (items.length === 0) return null

  return <ChipList title={title} items={items} />
}

export const medioEnvioPropType = PropTypes.shape({
  idMedioEnvio: PropTypes.number,
  descZona: PropTypes.string,
  cantDias: PropTypes.number,
  nombre: PropTypes.string,
  medioEnvio: PropTypes.shape({
    nombre: PropTypes.string
  })
})

MediosEnvioChips.propTypes = {
  mediosEnvios: PropTypes.arrayOf(medioEnvioPropType),
  listaEnvios: PropTypes.array,
  title: PropTypes.string
}
